import { DownOutlined, PlusOutlined } from '@ant-design/icons'
import { gql, useMutation, useQuery } from '@apollo/client'
import { Button, Col, Divider, Dropdown, Empty, Input, List, Menu, Row, Select, Space, Tag } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { EVENEMENT_UPDATE_MUTATION } from '../../apollo/mutation/evenement.mutation'
import { COLOR } from '../../constants'
import { generateLegacyLink } from '../../helper'
import { useAuth, useMatchCopro } from '../../lib/hooks'
import {
  D4_Evenementdemande_Set_Input,
  D4_Fn_View_Demande,
  D4_Fn_View_Evenements,
  D4_Tagevenement,
  D4_Tagevenement_Evenement,
  D4_Tagevenement_Evenement_Insert_Input,
  D4_Tagevenement_Insert_Input,
} from '../../types'
import D4Avatar from '../D4Avatar'
import { DDevModalForm } from '../DDevForm'
import { DIModalForm } from '../DIForm'
import Spinner from '../Spinner'
import DemandeContentItem from './DemandeContentItem'
import ModalEnvoyerDevis from './ModalEnvoyerDevis'

const TAGS_QUERY = gql`
  query Tags {
    tags: d4_tagevenement {
      id
      libelle
      created_at
    }
  }
`

const TAG_MUTATION = gql`
  mutation Tag($data: d4_tagevenement_insert_input!) {
    insert_d4_tagevenement_one(object: $data) {
      id
      libelle
    }
  }
`

const TAG_EVENEMENT_MUTATION = gql`
  mutation TagEvenement($data: [d4_tagevenement_evenement_insert_input!]!) {
    insert_d4_tagevenement_evenement(objects: $data) {
      affected_rows
    }
  }
`

const REMOVE_TAG_MUTATION = gql`
  mutation RemoveTag($id: Int!) {
    delete_d4_tagevenement_evenement_by_pk(id: $id) {
      id
    }
  }
`

const DemandeEvenementTitle = ({ item, demande }: { item: D4_Fn_View_Evenements; demande: D4_Fn_View_Demande }) => {
  const copro = useMatchCopro()
  const { data: tags } = useQuery<{ tags: D4_Tagevenement[] }>(TAGS_QUERY)
  const [addTag] = useMutation<{}, { data: D4_Tagevenement_Insert_Input }>(TAG_MUTATION)
  const [addTagToEvt] = useMutation<{}, { data: D4_Tagevenement_Evenement_Insert_Input }>(TAG_EVENEMENT_MUTATION)
  const [removeTagFromEvt] = useMutation<{ data: D4_Tagevenement_Evenement }, { id: number }>(REMOVE_TAG_MUTATION)
  const [name, setName] = useState('')
  const [diModalVisible, setDiModalVisible] = useState(false)
  const [mailModalVisible, setMailModalVisible] = useState(false)
  const [autreFourModalVisible, setAutreFourModalVisible] = useState(false)

  let menu = null

  if (item.interventionid) {
    menu = (
      <Menu>
        <Menu.Item key="1">Intervention terminée</Menu.Item>
        <Menu.Item key="2">
          <Link
            to={generateLegacyLink({
              path: 'detail_demande_intervention.jsp',
              url: copro?.url,
              id: item.interventionid,
            })}
            target="_blank"
            rel="noreferrer"
          >
            Voir détails
          </Link>
        </Menu.Item>
        <Menu.Item key="3">
          <Link
            to={generateLegacyLink({ path: 'v2/choix_cs_point_di.jsp', url: copro?.url, id: item.interventionid })}
            target="_blank"
            rel="noreferrer"
          >
            Faire point CS
          </Link>
        </Menu.Item>
        <Menu.Item key="4">
          <Link
            to={generateLegacyLink({
              path: 'v2/choix_d4_point_di_fournisseur.jsp',
              url: copro?.url,
              id: item.interventionid,
            })}
            target="_blank"
            rel="noreferrer"
          >
            Faire point Fournisseur
          </Link>
        </Menu.Item>
      </Menu>
    )
  } else if (item.demandedevisid) {
    menu = (
      <Menu>
        <Menu.Item key="1">Renvoyer au fournisseur</Menu.Item>
        <Menu.Item key="2">
          <Link
            to={generateLegacyLink({
              path: 'detail_demande_devis_fournisseur.jsp',
              url: copro?.url,
              id: item.demandedevisfournisseurid,
            })}
            target="_blank"
            rel="noreferrer"
          >
            Voir détails
          </Link>
        </Menu.Item>
        <Menu.Item key="3">
          <Link
            to={generateLegacyLink({
              path: 'v2/choix_d4_point_ddev_fournisseur.jsp',
              url: copro?.url,
              id: item.demandedevisfournisseurid,
            })}
            target="_blank"
            rel="noreferrer"
          >
            Faire point fournisseur
          </Link>
        </Menu.Item>
        <Menu.Item key="4">
          <Link
            to={generateLegacyLink({
              path: 'v2/ajouter_devis_ddev.jsp',
              url: copro?.url,
              id: item.demandedevisfournisseurid,
            })}
            target="_blank"
            rel="noreferrer"
          >
            Rajouter un devis
          </Link>
        </Menu.Item>
        <Menu.Item key="5">
          <Link
            to={generateLegacyLink({
              path: 'demande_devis_ajout_fournisseur.jsp',
              url: copro?.url,
              id: item.demandedevisid,
            })}
            target="_blank"
            rel="noreferrer"
          >
            Ajouter un fournisseur
          </Link>
        </Menu.Item>
      </Menu>
    )
  } else if (item.dtype === 'FichierDevisFournisseur') {
    menu = (
      <Menu>
        <Menu.Item key="2" onClick={() => setMailModalVisible(true)}>
          Envoyer au CS
        </Menu.Item>
        <Menu.Item key="3" onClick={() => setDiModalVisible(true)}>
          Donner BPA
        </Menu.Item>
        <Menu.Item key="4">Voir detail devis</Menu.Item>
        <Menu.Item
          key="5"
          onClick={() => {
            setAutreFourModalVisible(true)
          }}
        >
          Demander autre fournisseur
        </Menu.Item>
      </Menu>
    )
  }

  const handleSelect = useCallback(
    async (value: number) => {
      await addTagToEvt({ variables: { data: { evenement_id: item.id, tag_id: value } } })
    },
    [addTagToEvt, item.id]
  )

  const handleDeselect = useCallback(
    async (value: number) => {
      await removeTagFromEvt({ variables: { id: item.tags.find((t) => t.tag_id === value)?.id ?? -1 } })
    },
    [item.tags, removeTagFromEvt]
  )

  const addItem = async () => {
    await addTag({ variables: { data: { libelle: name } }, refetchQueries: [{ query: TAGS_QUERY }] })
    setName('')
  }

  return (
    <Row gutter={[12, 0]} justify="space-between">
      <Col>
        <Col>
          {item.auteurprenom} {item.auteurnom} - {dayjs(item.dateevenement).format('DD/MM/YYYY HH:mm')}
        </Col>
        <Col>
          {menu && (
            <Dropdown overlay={menu} overlayStyle={{ zIndex: 5000 }} trigger={['click']}>
              <Tag
                style={{ cursor: 'pointer' }}
                icon={<DownOutlined />}
                color={
                  item.dtype === 'FichierDevisFournisseur' ? COLOR.red : item.interventionid ? COLOR.blue : COLOR.green
                }
              >
                {item.dtype === 'FichierDevisFournisseur'
                  ? 'Devis'
                  : item.interventionid
                  ? 'Demande intervention'
                  : 'Demande de devis'}
              </Tag>
            </Dropdown>
          )}
        </Col>
      </Col>
      <Col>
        <Space>
          <Select
            options={tags?.tags
              .map((t) => ({ value: t.id, label: t.libelle }))
              .sort((p: any, n: any) => (p.label < n.label ? -1 : 1))}
            placeholder="Tags"
            mode="multiple"
            style={{ minWidth: 200 }}
            defaultValue={item.tags.map((t) => t.tagevenement.id)}
            onSelect={handleSelect}
            onDeselect={handleDeselect}
            tokenSeparators={[',']}
            dropdownRender={(m) => (
              <div>
                {m}
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                  <Input style={{ flex: 'auto' }} value={name} onChange={(e) => setName(e.target.value)} />
                  <Button onClick={addItem} type="link" icon={<PlusOutlined />}>
                    Ajouter
                  </Button>
                </div>
              </div>
            )}
          />
        </Space>
      </Col>
      <ModalEnvoyerDevis
        evenements={demande.evenements}
        visible={mailModalVisible}
        onOk={() => setMailModalVisible(false)}
        onCancel={() => setMailModalVisible(false)}
        demande={demande}
        fichierId={item.id}
      />
      <DDevModalForm
        demande={demande}
        fichierId={item.id}
        onCancel={() => setAutreFourModalVisible(false)}
        visible={autreFourModalVisible}
        width="100%"
        footer={null}
      />
      <DIModalForm
        demande={demande}
        visible={diModalVisible}
        footer={null}
        onCancel={() => setDiModalVisible(false)}
        width="100%"
        fichierId={item.id}
      />
    </Row>
  )
}

const DemandeContent = ({ loading, demande }: { loading: boolean; demande: D4_Fn_View_Demande | undefined }) => {
  const [updateEvent] = useMutation<{}, { id: number; data: D4_Evenementdemande_Set_Input }>(
    EVENEMENT_UPDATE_MUTATION,
    {
      update: (cache, { data }: any) => {
        cache.modify({
          id: cache.identify({ id: data.evenement.id, __typename: 'd4_fn_view_evenements' }),
          fields: {
            texte() {
              return data.evenement.texte
            },
          },
        })
      },
    }
  )
  const { user }: Auth = useAuth()

  if (loading) {
    return <Spinner />
  }
  if (!demande) {
    return <Empty description="Pas de demande selectionée" />
  }

  return (
    <List
      itemLayout="vertical"
      dataSource={[...(demande?.evenements ?? [])].reverse()}
      size="small"
      renderItem={(item: D4_Fn_View_Evenements) => (
        <List.Item>
          <List.Item.Meta
            title={<DemandeEvenementTitle item={item} demande={demande} />}
            avatar={
              <D4Avatar
                role={item.auteurtype?.toLowerCase() as 'gestionnaire' | 'personne'}
                nom={item.auteurnom}
                prenom={item.auteurprenom}
              />
            }
          />
          <DemandeContentItem item={item} updateEvent={updateEvent} currentUserId={Number(user?.personne.id)} />
        </List.Item>
      )}
    />
  )
}

export default DemandeContent
